import { useState, useEffect } from 'react';
import axios from 'axios';
import { Crosshair, TrendingDown, Calendar, CheckCircle, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TargetSetting = () => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTargets();
  }, []);

  const fetchTargets = async () => {
    try {
      const response = await axios.get(`${API}/emission-targets`);
      setTargets(response.data);
    } catch (error) {
      console.error('Error fetching emission targets:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScopeColor = (scope) => {
    if (scope.includes('Scope 1') && scope.includes('Scope 2')) return '#1A362D';
    if (scope.includes('Scope 1')) return '#C85A32';
    if (scope.includes('Scope 2')) return '#7898A5';
    if (scope.includes('Scope 3')) return '#8B9D8F';
    return '#1A362D';
  };

  const totalBase = targets.find(t => t.scope === 'Total (All Scopes)')?.base_emissions || 0;
  const totalTarget2030 = targets.filter(t => t.target_year === 2030 && !t.scope.includes('Total')).reduce((sum, t) => sum + t.target_emissions, 0);
  const netZeroTarget = targets.find(t => t.scope === 'Total (All Scopes)')?.target_emissions || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A362D]"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="target-setting-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-[#111814] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Emission Reduction Targets
        </h1>
        <p className="text-base text-[#4B5563] leading-relaxed">
          Science-Based Targets aligned with 1.5°C pathway (SBTi approach - simplified)
        </p>
      </div>

      {/* SBTi Alignment Banner */}
      <Card className="border-[#1A362D] border-2 mb-8 bg-gradient-to-r from-[#1A362D]/5 to-[#2D4A3E]/5" data-testid="sbti-banner">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#1A362D] rounded-lg flex items-center justify-center">
              <Crosshair className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Science-Based Targets</CardTitle>
              <CardDescription>Aligned with Science-Based Targets initiative (SBTi) methodology</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border border-[#E5E1D8]">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-1">Current Baseline (2024)</p>
              <p className="text-2xl font-bold text-[#111814]">{totalBase.toLocaleString()}</p>
              <p className="text-sm text-[#6B7280]">kg CO₂e</p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-[#C85A32]">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#C85A32] mb-1">2030 Target</p>
              <p className="text-2xl font-bold text-[#C85A32]">{totalTarget2030.toLocaleString()}</p>
              <p className="text-sm text-[#6B7280]">kg CO₂e (42% reduction)</p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-[#2D4A3E]">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#2D4A3E] mb-1">2050 Net Zero</p>
              <p className="text-2xl font-bold text-[#2D4A3E]">{netZeroTarget.toLocaleString()}</p>
              <p className="text-sm text-[#6B7280]">kg CO₂e (90% reduction)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Target Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {targets.filter(t => t.target_year === 2030).map((target, index) => (
          <Card key={index} className="border-[#E5E1D8]" data-testid={`target-card-${index}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: getScopeColor(target.scope) }}
                    ></div>
                    <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {target.scope}
                    </CardTitle>
                  </div>
                  <CardDescription>{target.pathway}</CardDescription>
                </div>
                {target.sbti_aligned && (
                  <Badge className="bg-[#2D4A3E] text-white">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    SBTi Aligned
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Timeline */}
              <div className="flex items-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-xs text-[#6B7280]">Base Year</p>
                  <p className="text-lg font-bold text-[#111814]">{target.base_year}</p>
                </div>
                <div className="flex-1 h-2 bg-[#F2F0EB] rounded-full relative">
                  <div 
                    className="absolute h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: '30%',
                      backgroundColor: getScopeColor(target.scope)
                    }}
                  ></div>
                  <TrendingDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2D4A3E]" />
                </div>
                <div className="text-center">
                  <p className="text-xs text-[#6B7280]">Target Year</p>
                  <p className="text-lg font-bold text-[#111814]">{target.target_year}</p>
                </div>
              </div>

              {/* Values */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-[#F9F8F6] rounded-lg">
                  <p className="text-xs text-[#6B7280] mb-1">Base Emissions</p>
                  <p className="text-lg font-bold text-[#111814]">{target.base_emissions.toLocaleString()}</p>
                  <p className="text-xs text-[#6B7280]">kg CO₂e</p>
                </div>
                <div className="p-3 bg-[#C85A32]/10 rounded-lg">
                  <p className="text-xs text-[#C85A32] mb-1">Reduction</p>
                  <p className="text-lg font-bold text-[#C85A32]">-{target.reduction_percent}%</p>
                  <p className="text-xs text-[#6B7280]">by {target.target_year}</p>
                </div>
                <div className="p-3 bg-[#2D4A3E]/10 rounded-lg">
                  <p className="text-xs text-[#2D4A3E] mb-1">Target</p>
                  <p className="text-lg font-bold text-[#2D4A3E]">{target.target_emissions.toLocaleString()}</p>
                  <p className="text-xs text-[#6B7280]">kg CO₂e</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Net Zero 2050 Card */}
      {targets.filter(t => t.target_year === 2050).map((target, index) => (
        <Card key={index} className="border-[#1A362D] border-2 mb-8" data-testid="net-zero-target-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1A362D] rounded-full flex items-center justify-center">
                <Crosshair className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Net Zero 2050</CardTitle>
                <CardDescription>{target.pathway}</CardDescription>
              </div>
              <Badge className="ml-auto bg-[#1A362D] text-white">Long-term Target</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#6B7280]">Progress to Net Zero</span>
                  <span className="text-sm font-semibold text-[#1A362D]">0% (Baseline set)</span>
                </div>
                <div className="relative">
                  <Progress value={0} className="h-4" />
                  <div className="absolute top-full mt-1 flex justify-between w-full text-xs text-[#6B7280]">
                    <span>2024</span>
                    <span>2030</span>
                    <span>2040</span>
                    <span>2050</span>
                  </div>
                </div>
              </div>
              <div className="text-center p-4 bg-[#1A362D]/5 rounded-lg">
                <p className="text-xs text-[#6B7280] mb-1">Target</p>
                <p className="text-3xl font-bold text-[#1A362D]">{target.reduction_percent}%</p>
                <p className="text-xs text-[#6B7280]">Reduction</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Methodology Note */}
      <Card className="border-[#E5E1D8]" data-testid="methodology-note">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-[#7898A5]" />
            <CardTitle className="text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Target Setting Methodology</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-[#F9F8F6] rounded-lg">
              <h4 className="font-semibold text-[#111814] mb-2">1.5°C Pathway Alignment</h4>
              <p className="text-sm text-[#4B5563] leading-relaxed">
                Targets are set following SBTi's 1.5°C pathway, which requires approximately 4.2% annual linear reduction 
                for Scope 1 & 2 emissions to achieve at least 42% reduction by 2030 from a 2020 baseline.
              </p>
            </div>
            <div className="p-4 bg-[#F9F8F6] rounded-lg">
              <h4 className="font-semibold text-[#111814] mb-2">Scope 3 Targets</h4>
              <p className="text-sm text-[#4B5563] leading-relaxed">
                For companies where Scope 3 emissions are more than 40% of total emissions, SBTi requires setting 
                Scope 3 targets covering at least 67% of value chain emissions.
              </p>
            </div>
            <div className="p-4 bg-[#7898A5]/10 rounded-lg border border-[#7898A5]/30">
              <p className="text-sm text-[#4B5563]">
                <span className="font-semibold">Note:</span> This is a simplified demonstration of SBTi methodology. 
                For official SBTi validation, detailed target setting criteria and third-party verification are required.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TargetSetting;
